import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import jwt_decode from "jwt-decode";
import Navbar from "../Components/Navbar";
import SearchBar from "../Components/SearchBar";
import HelpMessage from "../Components/HelpMessage";

const defaultMeta = {
  recipient: "",
  recipientCode: "",
  formDate: "",
  dateSubmitted: "",
};

const formatDisplayDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return value;
  }
};

const toInputDate = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

const buildBlankValues = (form) => {
  const blank = {};
  (form?.items || []).forEach((item) => {
    if (item.label) {
      blank[item.label] = "";
    }
  });
  return blank;
};

const buildItemPayload = (form, valueMap) => {
  const labels = [];
  const values = [];
  for (let index = 0; index < 10; index += 1) {
    const itemDef = form?.items?.[index];
    const label = itemDef?.label || "";
    labels.push(label);
    values.push(label ? valueMap[label] ?? "" : "");
  }
  return { labels, values };
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getStoredSupplierCode = () => {
  const stored = localStorage.getItem("currentUser");
  return stored ? stored.trim() : "";
};

const Forms = () => {
  const API_BASE = process.env.REACT_APP_API_BASEURL;

  const [supplierCode, setSupplierCode] = useState(getStoredSupplierCode());
  const [isAdmin, setIsAdmin] = useState(false);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwt_decode(token);
        const userRole = decoded?.role || "";
        setIsAdmin(userRole.toLowerCase().includes("admin"));
        setUserId(decoded?.user || null);
      } catch (error) {
        setIsAdmin(false);
        setUserId(null);
      }
    }
  }, []);

  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [formMode, setFormMode] = useState("view");
  const [draftValues, setDraftValues] = useState({});
  const [meta, setMeta] = useState({
    ...defaultMeta,
    recipient: supplierCode,
  });
  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const handleSupplierChange = (event) => {
      const nextCode =
        event?.detail?.supplier?.trim() || getStoredSupplierCode();
      setSupplierCode(nextCode);
    };

    window.addEventListener("supplierContextChanged", handleSupplierChange);
    return () => {
      window.removeEventListener("supplierContextChanged", handleSupplierChange);
    };
  }, []);

  /* no sticky behavior */

  useEffect(() => {
    const fetchForms = async () => {
      try {
        setLoading(true);
        const config = {
          headers: getAuthHeaders(),
        };
        // Always send supplier for company resolution
        // Use supplierCode if set, otherwise fall back to userId (for admins)
        const supplierForContext = supplierCode || userId;
        if (supplierForContext) {
          config.params = { supplier: supplierForContext };
        }
        const response = await axios.get(`${API_BASE}/api/forms`, config);
        const payload = response.data.forms || [];
        setForms(payload);
        if (payload.length) {
          setSelectedFormId(payload[0].id);
        }
      } catch (err) {
        console.error("Error loading forms", err);
        setError("Unable to load forms right now.");
      } finally {
        setLoading(false);
      }
    };

    fetchForms();
  }, [API_BASE, supplierCode, userId, isAdmin]);

  const selectedForm = useMemo(
    () => forms.find((form) => form.id === selectedFormId) || null,
    [forms, selectedFormId]
  );

  const resetEditorForForm = (form) => {
    setDraftValues(buildBlankValues(form));
  setMeta({
      ...defaultMeta,
      recipient: supplierCode,
    });
    setSelectedSubmission(null);
    setFormMode("view");
  };

  const applySubmission = (submission) => {
    if (!submission || !selectedForm) return;

    const nextValues = buildBlankValues(selectedForm);
    (selectedForm.items || []).forEach((item, index) => {
      nextValues[item.label] = submission.values?.[index] ?? "";
    });

    setDraftValues(nextValues);
    setMeta({
      recipient: submission.recipient || supplierCode,
      recipientCode: "",
      formDate: submission.formDate
        ? new Date(submission.formDate).toISOString()
        : "",
      dateSubmitted: submission.dateSubmitted
        ? new Date(submission.dateSubmitted).toISOString()
        : "",
    });
    setSelectedSubmission(submission);
    setFormMode("view");
  };

  const applyFromList = (list, focusId) => {
    if (!list || list.length === 0) {
      resetEditorForForm(selectedForm);
      return;
    }
    const target = focusId
      ? list.find((row) => row.id === focusId)
      : list[0];
    applySubmission(target || list[0]);
  };

  const loadSubmissions = async (form, focusId) => {
    if (!form?.company || !form?.formName) {
      setSubmissions([]);
      resetEditorForForm(form);
      return;
    }

    try {
      setSubmissionsLoading(true);
      const config = { headers: getAuthHeaders() };
      // Always send supplier for company resolution
      // For admins: backend will return all company submissions if no supplier param
      // For regular users: backend filters by supplier
      // If admin wants all submissions, don't send supplier param
      if (supplierCode) {
        // For admins, only send supplier if they want to filter by that supplier
        // Otherwise, omit it to get all company submissions
        if (!isAdmin) {
          config.params = { supplier: supplierCode };
        }
        // For admins: if supplierCode is set but we want all, don't send param
        // The backend will use the supplier from context for company resolution
        // but won't filter submissions if no supplier query param is provided
      }
      const response = await axios.get(
        `${API_BASE}/api/forms/${encodeURIComponent(form.company)}/${encodeURIComponent(
          form.formName
        )}/submissions`,
        config
      );
      const rows = response.data.submissions || [];
      setSubmissions(rows);
      applyFromList(rows, focusId);
    } catch (err) {
      console.error("Error loading form submissions", err);
      setSubmissions([]);
      resetEditorForForm(form);
      if (err.response?.status !== 404) {
        toast.error("Unable to load form submissions.");
      }
    } finally {
      setSubmissionsLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedForm) {
      setSubmissions([]);
      resetEditorForForm(null);
      return;
    }

    resetEditorForForm(selectedForm);
    loadSubmissions(selectedForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedForm, supplierCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartNew = () => {
    if (!selectedForm) return;
    resetEditorForForm(selectedForm);
  setMeta({
    recipient: supplierCode,
    recipientCode: "",
    formDate: "",
    dateSubmitted: new Date().toISOString(),
  });
    setFormMode("create");
  };

  const handleStartEdit = (row) => {
    if (!row) {
      toast.info("Select a submission to edit.");
      return;
    }
    applySubmission(row);
    setFormMode("edit");
  };

  const handleDelete = async (row) => {
    if (!selectedForm || !row) {
      toast.info("Select a submission to delete.");
      return;
    }

    const confirmDelete = window.confirm("Delete this submission?");
    if (!confirmDelete) {
      return;
    }

    try {
      setDeleting(true);
      const config = {
        headers: getAuthHeaders(),
        data: {
          recipient: row.recipient || supplierCode,
        },
      };
      if (supplierCode) {
        config.params = { supplier: supplierCode };
      }
      const response = await axios.delete(
        `${API_BASE}/api/forms/${encodeURIComponent(selectedForm.company)}/${encodeURIComponent(
          selectedForm.formName
        )}/submissions/${encodeURIComponent(row.id)}`,
        config
      );
      toast.success("Submission deleted.");
      const rows = response.data.submissions || [];
      setSubmissions(rows);
      applyFromList(rows);
      setFormMode("view");
    } catch (err) {
      console.error("Error deleting submission", err);
      toast.error(err.response?.data?.message || "Unable to delete submission.");
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    if (formMode === "edit" && selectedSubmission) {
      applySubmission(selectedSubmission);
    } else {
      resetEditorForForm(selectedForm);
    }
    setFormMode("view");
  };

  const handleMetaChange = (field, value) => {
    setMeta((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!selectedForm || formMode === "view") {
      return;
    }

    try {
      setSaving(true);
      const { labels, values } = buildItemPayload(selectedForm, draftValues);
    const resolvedDateSubmitted =
      meta.dateSubmitted || new Date().toISOString();
      const payload = {
        recipient: meta.recipient || supplierCode,
        recipientCode: meta.recipientCode,
        formDate: meta.formDate || null,
      dateSubmitted: resolvedDateSubmitted,
        items: labels,
        values,
      };

      let response;
      const requestConfig = { headers: getAuthHeaders() };
      if (supplierCode) {
        requestConfig.params = { supplier: supplierCode };
      }

      if (formMode === "edit" && selectedSubmission?.id) {
        response = await axios.put(
          `${API_BASE}/api/forms/${encodeURIComponent(selectedForm.company)}/${encodeURIComponent(
            selectedForm.formName
          )}/submissions/${encodeURIComponent(selectedSubmission.id)}`,
          payload,
          requestConfig
        );
        toast.success("Submission updated.");
      } else {
        response = await axios.post(
          `${API_BASE}/api/forms/${encodeURIComponent(selectedForm.company)}/${encodeURIComponent(selectedForm.formName)}/submissions`,
          payload,
          requestConfig
        );
        toast.success("Submission saved.");
      }

      const rows = response.data.submissions || [];
      setSubmissions(rows);
      const focusId = response.data.createdId || response.data.updatedId;
      applyFromList(rows, focusId);
      setFormMode("view");
    } catch (err) {
      console.error("Error saving submission", err);
      toast.error(err.response?.data?.message || "Unable to save submission.");
    } finally {
      setSaving(false);
    }
  };

  const handleSelectSubmission = (row) => {
    applySubmission(row);
  };

  return (
    <div className="customer-layout">
      <Navbar />
      <main className="customer-main">
        <div className="page-toolbar">
          <SearchBar />
        </div>

        <section className="forms-stack">
          <div className="forms-card">
            <header className="forms-card__header">
              <div className="card-header--stacked">
                <h1 className="page-hero__title">Forms</h1>
              </div>
            </header>

            {loading ? (
              <div className="notes-empty">Loading forms…</div>
            ) : error ? (
              <div className="notes-empty">{error}</div>
            ) : forms.length === 0 ? (
              <div className="notes-empty">No forms have been published yet.</div>
            ) : (
              <div className="forms-master__table">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Date</th>
                      <th scope="col">Name</th>
                      <th scope="col">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {forms.map((form) => (
                      <tr
                        key={form.id}
                        className={form.id === selectedFormId ? "active" : ""}
                        onClick={() => setSelectedFormId(form.id)}
                      >
                        <td>{formatDisplayDate(form.date)}</td>
                        <td>{form.name}</td>
                        <td>{form.type || "Supplier"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {!selectedForm ? (
            <div className="forms-card">
              <div className="notes-empty">Select a form to view its details.</div>
            </div>
          ) : (
            <>
              <div className="forms-card">
                <header className="forms-card__header">
                  <div>
                    <h3>Entries for {selectedForm.name}</h3>
                    <p>
                      Showing saved submissions. Tap a row to review, or start a new one.
                    </p>
                  </div>
                  <button
                    className="button secondary sm"
                    onClick={handleStartNew}
                    disabled={saving || deleting}
                  >
                    New Entry
                  </button>
                </header>
                {submissionsLoading ? (
                  <div className="notes-empty">Loading entries…</div>
                ) : submissions.length === 0 ? (
                  <div className="notes-empty">No entries recorded yet.</div>
                ) : (
                  <div className="forms-data-table">
                    <table>
                      <thead>
                        <tr>
                          <th scope="col">Date Submitted</th>
                          {(selectedForm.items || []).map((item) => (
                            <th key={item.label} scope="col">
                              {item.label}
                            </th>
                          ))}
                          <th scope="col" className="forms-data-actions-col">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {submissions.map((row) => (
                          <tr
                            key={row.id}
                            className={
                              selectedSubmission?.id === row.id ? "selected" : ""
                            }
                            onClick={() => handleSelectSubmission(row)}
                          >
                            <td>{formatDisplayDate(row.dateSubmitted)}</td>
                            {(selectedForm.items || []).map((item, index) => (
                              <td key={`${row.id}-${item.label}`}>
                                {row.values?.[index] ?? "—"}
                              </td>
                            ))}
                            <td>
                              <div className="forms-data-actions">
                                <button
                                  type="button"
                                  className="icon-button"
                                  title="Edit entry"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStartEdit(row);
                                  }}
                                  disabled={saving || deleting}
                                >
                                  <i className="uicon-edit" />
                                </button>
                                <button
                                  type="button"
                                  className="icon-button danger"
                                  title="Delete entry"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(row);
                                  }}
                                  disabled={saving || deleting}
                                >
                                  <i className="uicon-trash" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="forms-card">
                {formMode === "view" && !selectedSubmission ? (
                  <div className="notes-empty">
                    Select an entry above or choose New Entry to fill out this form.
                  </div>
                ) : (
                  <>
                    <header className="forms-card__header">
                      <div>
                        <h3>
                          {formMode === "create"
                            ? "Add new entry"
                            : formMode === "edit"
                            ? "Edit entry"
                            : "Form entry"}
                        </h3>
                        <p>
                          {formMode === "view"
                            ? "Review the saved values below."
                            : "Complete the fields and save when ready."}
                        </p>
                      </div>
                    </header>

                    <div className="forms-editor-grid">
                      {(selectedForm.items || []).map((item) => (
                        <label key={item.label} className="forms-editor-field">
                          <span>{item.label}</span>
                          <input
                            type="text"
                            value={draftValues[item.label] ?? ""}
                            onChange={(e) =>
                              setDraftValues((prev) => ({
                                ...prev,
                                [item.label]: e.target.value,
                              }))
                            }
                            disabled={formMode === "view"}
                          />
                        </label>
                      ))}
                    </div>
                    <div className="forms-editor-actions">
                      <button
                        className="button"
                        onClick={handleSave}
                        disabled={formMode === "view" || saving}
                      >
                        {saving ? "Saving…" : "Save"}
                      </button>
                      <button
                        className="button ghost"
                        onClick={handleCancel}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </section>

        <div className="home-help">
          <HelpMessage />
        </div>
      </main>
    </div>
  );
};

export default Forms;
